import { useState, MouseEvent } from "react";
import { ActivityShellAction, ActivityShellProps } from "./ActivityShellTypes";
import TooltipComponent from "../../../../shared/TooltipComponent";
import DynamicIcon from "../../../../../components/DynamicIcon";
import FloatingPanelService from "../../../../shared/FloatingPanel";
import { TranslationService } from "../../../../../services/TranslationService";
import { useActivityView } from "../ActivityViewContext";

export const ActivityItemShell = ({
  cardClassName,
  iconNode,
  headerNode,
  dateNode,
  content,
  actions,
  expandable = false,
  expandedContent,
  onExpand,
  aiStripNode,
  compactLine,
  respectViewContext = true
}: ActivityShellProps) => {
  const [showMore, setShowMore] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isCompactOpen, setIsCompactOpen] = useState(false);
  const [executingIndex, setExecutingIndex] = useState<number | null>(null);
  const { translate } = TranslationService;
  const { view: contextView } = useActivityView();
  const view = respectViewContext ? contextView : "detallada";

  const handleClose = () => {
    setShowMore(false);
  };

  const handleToggle = async () => {
    if (!showMore && onExpand) {
      await onExpand();
    }
    setShowMore((prev) => !prev);
  };

  const handleCompactOpen = async () => {
    if (expandable && onExpand) {
      await onExpand();
    }
    setIsCompactOpen(true);
  };

  const handleCompactClose = (e: MouseEvent) => {
    e.stopPropagation();
    setIsCompactOpen(false);
  };

  const runAction = async (
    e: MouseEvent<HTMLButtonElement>,
    idx: number,
    action: ActivityShellAction
  ) => {
    e.stopPropagation();
    setExecutingIndex(idx);
    try {
      FloatingPanelService.hidePanel();
      const isEditAction =
        typeof action.icon === "string" &&
        action.icon.includes("fa-pen text-secondary");
      const result = isEditAction
        ? action.onClick(handleClose)
        : action.onClick();
      if (result && typeof (result as Promise<void>).then === "function") {
        await (result as Promise<void>);
      }
    } finally {
      setExecutingIndex(null);
    }
  };

  if (view === "compacta" && !isCompactOpen) {
    return (
      <div
        className="historyCard--compact d-flex align-items-center gap-2 pointer"
        onClick={handleCompactOpen}
      >
        <div style={{ flex: "0 0 auto" }}>{iconNode}</div>
        <div
          style={{
            flex: "1 1 auto",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
        >
          {compactLine}
        </div>
        <div style={{ flex: "0 0 auto" }}>{dateNode}</div>
      </div>
    );
  }

  const handleCardClick = () => {
    if (window.getSelection()?.toString()) return;
    handleToggle();
  };

  return (
    <div
      onClick={view === "detallada" && expandable ? handleCardClick : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cardClassName}
    >
      {aiStripNode}
      <div className="row">
        <div className="col-auto pe-0">{iconNode}</div>

        <div className="col pt-1">
          {headerNode}
          {content}
        </div>

        <div className="col-auto">
          {dateNode}

          <div className="row justify-content-end">
            <div className="actions justify-content-end d-flex gap-12 ms-3 mt-f">
              {executingIndex !== null && (
                <div
                  className="position-absolute top-0 end-0 p-2"
                  style={{ pointerEvents: "none" }}
                >
                  <i className="fas fa-spinner-third fa-spin" />
                </div>
              )}
              {actions.map((action, index) => {
                const isRunning = executingIndex === index;
                return (
                  <TooltipComponent title={action.tooltip ?? false} key={index}>
                    <button
                      type="button"
                      disabled={executingIndex !== null}
                      onClick={(e) => runAction(e, index, action)}
                      aria-busy={isRunning}
                    >
                      {isRunning ? (
                        <i className="fas fa-spinner-third fa-spin fa-fw" />
                      ) : (
                        <DynamicIcon
                          icon={action.icon}
                          className={action.classes}
                        />
                      )}
                    </button>
                  </TooltipComponent>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Contenido expandible en detallada */}
      {view === "detallada" && showMore && expandable && (
        <hr className="mb-0" />
      )}
      <div className="row">
        <div className="col-auto pe-0" style={{ width: 25 }} />
        <div
          className="col px-4 justify-content-center align-items-center"
          style={
            view === "detallada" && showMore && expandable
              ? { paddingBottom: "1.9rem" }
              : undefined
          }
        >
          {view === "detallada" && expandable && showMore && expandedContent}
        </div>
      </div>

      {/* Contenido expandible en compacta (siempre visible al abrir) */}
      {view === "compacta" &&
        isCompactOpen &&
        expandable &&
        expandedContent && (
          <>
            <hr className="mb-0" />
            <div className="row">
              <div className="col-auto pe-0" style={{ width: 25 }} />
              <div className="col px-4" style={{ paddingBottom: "1.9rem" }}>
                {expandedContent}
              </div>
            </div>
          </>
        )}

      {/* Hint "Ver más" en detallada */}
      {view === "detallada" && !showMore && expandable && (
        <div className="row" style={{ position: "relative" }}>
          <div
            className="col d-flex justify-content-center align-items-center"
            style={{
              position: "absolute",
              top: -15,
              bottom: 15,
              left: 0,
              right: 0
            }}
          >
            {isHovered && (
              <div className="seeMoreClose">
                <i className="fa fa-arrow-down mx-1" /> {translate.SeeMore}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Botón "Ver menos" en detallada */}
      {view === "detallada" && expandable && showMore && (
        <div className="row" style={{ position: "relative" }}>
          <div
            className="col d-flex justify-content-center align-items-center"
            style={{
              position: "absolute",
              top: -15,
              bottom: 15,
              left: 0,
              right: 0
            }}
          >
            <div className="seeMoreClose">
              <div className="mx-auto close-container">
                <button
                  type="button"
                  style={{ marginRight: "6%" }}
                  className="btn btn-link a-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                  }}
                >
                  <div className="seeMoreClose">
                    <i className="fa fa-arrow-up mx-1" /> {translate.SeeLess}
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Botón "Ver menos" en compacta */}
      {view === "compacta" && isCompactOpen && (
        <div className="d-flex justify-content-end mt-2">
          <button
            type="button"
            className="btn btn-link btn-sm a-button p-0"
            onClick={handleCompactClose}
          >
            <i className="fa fa-arrow-up mx-1" /> {translate.SeeLess}
          </button>
        </div>
      )}
    </div>
  );
};
